import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AbonnementProvider, useAbonnement } from './context/AbonnementContext';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import Profile from './pages/Profile';
import Messages from './pages/Messages';
import About from './pages/About';
import Fonctionnalités from './pages/Fonctionnalites';
import Comment from './pages/Comment';
import Blog from './pages/Blog';
import Contact from './pages/Contact';

import Abonnement from './pages/Abonnement';
import AbonnementConfirmation from './pages/AbonnementConfirmation';
import ScrollToTop from './components/ScrollToTop';

import Legal from './pages/Legal';
import MotDePasseOublie from './pages/MotDePasseOublie';
import ReinitialiserMotDePasse from './pages/ReinitialiserMotDePasse';
// ...

// ═══ Route protégée : connexion requise ═══
const PrivateRoute = ({ children, adminOnly }) => {
  const { user, loading } = useAuth();

  if (loading) return <div style={styles.loading}>Chargement...</div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== 'admin' && user.role !== 'moderateur') return <Navigate to="/dashboard" />;

  return children;
};

// ═══ Route protégée : abonnement requis ═══
// Redirige vers /abonnement si l'abonnement est obligatoire mais absent.
// Les administrateurs ne sont jamais bloqués.
const AbonnementRoute = ({ children }) => {
  const { user } = useAuth();
  const { actif, loading } = useAbonnement();

  if (loading) return <div style={styles.loading}>Chargement...</div>;
  if (user?.role === 'admin') return children;
  if (!actif) return <Navigate to="/abonnement" replace />;

  return children;
};

// ═══ Route de la page profil ═══
// - Membre non validé (en_attente / refuse) : accès libre pour compléter/corriger.
// - Membre validé : soumis à l'abonnement (femme = accès, homme non abonné = /abonnement).
const ProfileRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { actif, loading: aboLoading } = useAbonnement();

  if (loading || aboLoading) return <div style={styles.loading}>Chargement...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'admin' || user.role === 'moderateur') return children;

  // Statut bloquant (brouillon/en_attente/refuse) : accès libre au profil pour le compléter.
  const statutsBloquants = ['brouillon', 'en_attente', 'refuse'];
  if (statutsBloquants.includes(user.statut_profil)) return children;

  // Sinon (validé, ou statut absent = ancien membre) : contrôle d'abonnement (option 2).
  if (!actif) return <Navigate to="/abonnement" replace />;
  return children;
};

// ═══ Route protégée : profil validé requis ═══
// Tant que le profil n'est pas 'valide' (en_attente ou refuse), le membre
// est renvoyé sur /profile pour compléter/corriger et attendre l'examen.
// Les administrateurs ne sont jamais bloqués.
const ValidationRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <div style={styles.loading}>Chargement...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'admin' || user.role === 'moderateur') return children;
  // On ne bloque que si le statut est EXPLICITEMENT non validé.
  // Un statut absent (ancien compte, /auth/me pas encore revenu) ne bloque pas,
  // pour ne pas enfermer par erreur un membre déjà validé.
  const statutsBloquants = ['brouillon', 'en_attente', 'refuse'];
  if (statutsBloquants.includes(user.statut_profil)) return <Navigate to="/profile" replace />;

  return children;
};

function App() {
  return (
    <AuthProvider>
      <AbonnementProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* ═══ Pages publiques ═══ */}
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/comment" element={<Comment />} />
            <Route path="/fonctionnalites" element={<Fonctionnalités />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/mentions-legales" element={<Legal />} />
            <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
            <Route path="/reinitialiser-mot-de-passe" element={<ReinitialiserMotDePasse />} />
            {/* ═══ Abonnement : connexion requise, mais jamais bloqué ═══ */}
            <Route path="/abonnement" element={
              <PrivateRoute>
                <ValidationRoute>
                  <Abonnement />
                </ValidationRoute>
              </PrivateRoute>
            } />
            <Route path="/abonnement/confirmation" element={
              <PrivateRoute>
                <ValidationRoute>
                  <AbonnementConfirmation />
                </ValidationRoute>
              </PrivateRoute>
            } />

            {/* ═══ Dashboard : accessible sans abonnement ═══ */}
            {/* Les femmes non abonnées y accèdent (annuaire gratuit).      */}
            {/* Les hommes non abonnés sont redirigés vers /abonnement.     */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                <ValidationRoute>
                  <AbonnementRoute>
                    <Dashboard />
                  </AbonnementRoute>
                </ValidationRoute>
              </PrivateRoute>
            } />

            {/* ═══ Profil : abonnement requis ═══ */}
            {/* Un homme non abonné ne peut rien faire sans souscrire. */}
            <Route path="/profile" element={
              <PrivateRoute>
                <ProfileRoute>
                  <Profile />
                </ProfileRoute>
              </PrivateRoute>
            } />

            {/* ═══ Messages : abonnement requis ═══ */}
            {/* Une femme non abonnée y accède (son abonnement n'est pas requis). */}
            {/* Un homme non abonné est redirigé vers /abonnement.               */}
            <Route path="/messages" element={
              <PrivateRoute>
                <ValidationRoute>
                  <AbonnementRoute>
                    <Messages />
                  </AbonnementRoute>
                </ValidationRoute>
              </PrivateRoute>
            } />

            <Route path="/admin" element={
              <PrivateRoute adminOnly>
                <Admin />
              </PrivateRoute>
            } />

          </Routes>
        </BrowserRouter>
      </AbonnementProvider>
    </AuthProvider>
  );
}

const styles = {
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#4a0028',
  },
};

export default App;